'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import type { AuroraData } from '@/lib/aurora';
import {
  resolveAuroraLabels,
  type AuroraLabels,
} from '@/components/aurora/labels';

const STORAGE_KEY = 'aurora-alert-dismissed';

function isDismissedToday(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (!dismissed) return false;
    const today = new Date().toISOString().slice(0, 10);
    return dismissed === today;
  } catch {
    return false;
  }
}

function dismissAlert(): void {
  try {
    const today = new Date().toISOString().slice(0, 10);
    sessionStorage.setItem(STORAGE_KEY, today);
  } catch {
    // sessionStorage unavailable
  }
}

export default function AuroraAlert({ labels }: { labels?: AuroraLabels }) {
  const L = resolveAuroraLabels(labels);
  const [data, setData] = useState<AuroraData | null>(null);
  const [dismissed, setDismissed] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setDismissed(isDismissedToday());

    let cancelled = false;

    async function fetchAurora() {
      try {
        const res = await fetch('/api/aurora');
        if (!res.ok) throw new Error('Failed to fetch');
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        // Silently fail
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }

    fetchAurora();
    return () => { cancelled = true; };
  }, []);

  // Only show for amber or red
  if (!loaded || !data || (data.level !== 'amber' && data.level !== 'red')) return null;

  // Respect dismissal
  if (dismissed) return null;

  const isRed = data.level === 'red';

  const message = isRed ? L.alertRed : L.alertAmber;

  const gradientStyle = isRed
    ? { background: 'linear-gradient(135deg, #EF4444, #9333EA)' }
    : { background: 'linear-gradient(135deg, #F97316, #EA580C)' };

  return (
    <div
      className="relative w-full py-2 px-4 text-white overflow-hidden"
      style={gradientStyle}
    >
      {isRed && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)',
            animation: 'aurora-shimmer 3s ease-in-out infinite',
          }}
        />
      )}

      <style jsx>{`
        @keyframes aurora-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      <div className="relative flex items-center justify-between gap-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="shrink-0">🦊</span>
          <p className="text-sm font-medium truncate">{message}</p>
          <Link
            href="/aurora"
            className="shrink-0 text-sm font-semibold underline underline-offset-2 hover:no-underline"
          >
            {L.alertCta}
          </Link>
        </div>

        <button
          onClick={() => {
            dismissAlert();
            setDismissed(true);
          }}
          className="shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
          aria-label={L.alertDismiss}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>
    </div>
  );
}
